import React from 'react';
import { Image as ImageIcon, Phone, Video, FileText, ExternalLink } from 'lucide-react';
import { renderTemplatePreview } from './playbookPlaceholders';

/**
 * Compact WhatsApp-style preview bubble for the Shopify Playbook dialog.
 * Mirrors what the recipient will see — header / body / footer / buttons —
 * with user-supplied variables substituted live.
 *
 * Kept visually tight so it fits in the right column of a modal without
 * scrolling. Not a full-fidelity WhatsApp render; we skip things like
 * carousels, location headers, and long-press menus.
 */

interface MetaButton {
  type?: string;
  text?: string;
  url?: string;
  phone_number?: string;
}

interface MetaComponent {
  type?: string;
  format?: string;
  text?: string;
  buttons?: MetaButton[];
  example?: { header_handle?: string[] };
}

interface PlaybookPreviewProps {
  /** Components array from `template.meta_response.components`. */
  components: MetaComponent[];
  /** Variable map — key → substitution. */
  variables: Record<string, string>;
}

/**
 * Pull the first component of a given type (case-insensitive) from the list.
 * Exported so unit tests can poke at it without rendering the whole tree.
 */
export function findComponent(components: MetaComponent[], type: string): MetaComponent | null {
  if (!Array.isArray(components)) return null;
  return (
    components.find((c) => String(c?.type || '').toUpperCase() === type.toUpperCase()) || null
  );
}

/**
 * WhatsApp uses `*text*` for bold, `_text_` for italic, `~text~` for strike.
 * We render bold only (it's what 90% of templates use); other styles stay
 * as literal text. Return segments, not HTML, so React can handle escaping.
 */
export function renderWhatsAppFormatting(text: string): React.ReactNode[] {
  if (!text) return [];
  const parts: React.ReactNode[] = [];
  const regex = /\*([^*\n]+)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`b-${key++}`} className="font-semibold">
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function HeaderMediaPlaceholder({ format }: { format: string }) {
  const label =
    format === 'IMAGE' ? 'Image' :
    format === 'VIDEO' ? 'Video' :
    format === 'DOCUMENT' ? 'Document' :
    'Media';
  const Icon =
    format === 'IMAGE' ? ImageIcon :
    format === 'VIDEO' ? Video :
    FileText;
  return (
    <div className="flex items-center justify-center h-28 bg-gray-200 rounded-md text-gray-500 gap-1.5">
      <Icon className="h-4 w-4" />
      <span className="text-xs">{label} header</span>
    </div>
  );
}

export const PlaybookPreview: React.FC<PlaybookPreviewProps> = ({ components, variables }) => {
  const header = findComponent(components, 'HEADER');
  const body = findComponent(components, 'BODY');
  const footer = findComponent(components, 'FOOTER');
  const buttonsComp = findComponent(components, 'BUTTONS');

  const headerFormat = String(header?.format || 'TEXT').toUpperCase();
  const headerText = headerFormat === 'TEXT' && header?.text
    ? renderTemplatePreview(header.text, variables)
    : null;
  const bodyText = body?.text ? renderTemplatePreview(body.text, variables) : '';
  const footerText = footer?.text || '';
  const buttons = Array.isArray(buttonsComp?.buttons) ? buttonsComp!.buttons! : [];

  return (
    <div
      className="relative rounded-2xl bg-[#E6DDD4] p-4 shadow-inner"
      data-testid="shopify-playbook-preview-bubble"
    >
      {/* Chat header strip — mimics a WhatsApp conversation */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center text-white shadow-sm">
          <Phone className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-semibold text-gray-700">WhatsApp Preview</div>
          <div className="text-[10px] text-gray-500">How your customer will see it</div>
        </div>
      </div>

      {/* Message bubble */}
      <div className="relative max-w-full rounded-lg bg-white shadow-sm p-3 ml-2 before:content-[''] before:absolute before:top-3 before:-left-1.5 before:w-3 before:h-3 before:bg-white before:rotate-45">
        {/* Header */}
        {header && headerFormat !== 'TEXT' && (
          <div className="mb-2">
            <HeaderMediaPlaceholder format={headerFormat} />
          </div>
        )}
        {headerText && (
          <div className="text-sm font-semibold text-gray-900 mb-1.5 break-words">
            {renderWhatsAppFormatting(String(headerText))}
          </div>
        )}

        {/* Body */}
        {bodyText ? (
          <div className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
            {renderWhatsAppFormatting(bodyText)}
          </div>
        ) : (
          <div className="text-xs text-gray-400 italic">No body text.</div>
        )}

        {/* Footer */}
        {footerText && (
          <div className="text-[11px] text-gray-500 mt-2 break-words">{footerText}</div>
        )}

        {/* Timestamp */}
        <div className="text-[10px] text-gray-400 text-right mt-1">12:34</div>
      </div>

      {/* Buttons — WhatsApp stacks these vertically below the bubble */}
      {buttons.length > 0 && (
        <div className="mt-2 ml-2 space-y-1">
          {buttons.map((btn, idx) => {
            const type = String(btn?.type || '').toUpperCase();
            const isUrl = type === 'URL';
            const isPhone = type === 'PHONE_NUMBER';
            const Icon = isUrl ? ExternalLink : isPhone ? Phone : null;
            return (
              <div
                key={`${type}-${idx}`}
                className="flex items-center justify-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm font-medium text-[#00A5F4] shadow-sm"
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                <span className="truncate">{btn?.text || 'Button'}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
